import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import SearchPending from "../../components/SearchPending";
import React, { useState } from "react";
import {
  applicationStatuses,
  sortApplicationsByStatus,
} from "../../utils/announcementManagement";
const stages = [
  "Sourcing",
  "Nouveau",
  "En attente",
  "Sélectionné",
  "Entretien tel",
  "RDV physique",
  "Shortlist",
  "Proposition",
  "Recruté",
  "Refusé",
];
const sources = [
  "Le site",
  "Mon Intérim",
  "France Travail",
  "HelloWork",
  "Candidature spontanée",
  "Recommandation",
  "Autre",
];
const field =
  "rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800";
const stamp = (value) => {
  const time = new Date(value || "").getTime();
  return Number.isFinite(time) ? time : 0;
};
const statusStyle = (status) =>
  status === "Refusé"
    ? "border-rose-200 bg-rose-50 text-rose-800"
    : status === "Recruté"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : "border-blue-200 bg-blue-50 text-blue-900";
export default function AnnouncementCandidates({
  applications,
  busy,
  onChangeStatus,
  onBulkStatus,
  onViewCv,
}) {
  const [filter, setFilter] = useState("all"),
    [search, setSearch] = useState(""),
    [sort, setSort] = useState("recent"),
    [view, setView] = useState("list");
  const [appliedSearch, searchPending] = useDebouncedValue(search);
  const [selectedIds, setSelection] = useState([]),
    [bulk, setBulk] = useState("");
  const allStages = [
    ...new Set([...stages, ...applications.map((c) => c.status)]),
  ];
  const counts = (status) =>
    applications.filter((c) => c.status === status).length;
  const normalize = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  let visible = applications.filter(
    (c) =>
      (filter === "all" || c.status === filter) &&
      normalize(
        [c.firstname, c.lastname, c.email, c.phone, c.town, c.source].join(" "),
      ).includes(normalize(appliedSearch)),
  );
  visible =
    sort === "status"
      ? sortApplicationsByStatus(visible)
      : [...visible].sort((a, b) =>
          sort === "name"
            ? `${a.lastname || ""} ${a.firstname || ""}`.localeCompare(
                `${b.lastname || ""} ${b.firstname || ""}`,
                "fr",
              )
            : sort === "oldest"
              ? stamp(a.date) - stamp(b.date)
              : stamp(b.date) - stamp(a.date),
        );
  const selection = selectedIds.filter(id => visible.some(candidate => String(candidate._id) === id));
  const chooseFilter = (value) => {
    setFilter(value);
    setSelection([]);
  };
  const toggle = (id) =>
    setSelection((current) =>
      current.includes(id) ? current.filter((v) => v !== id) : [...current, id],
    );
  const applyBulk = async () => {
    if (!bulk || !selection.length) return;
    const saved = await onBulkStatus(selection, bulk);
    if (saved) {
      setSelection([]);
      setBulk("");
    }
  };
  return (
    <section className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-bold text-slate-900">
            {applications.length} candidature
            {applications.length !== 1 ? "s" : ""}
          </h3>
          <div className="flex rounded-full bg-slate-100 p-1">
            {[
              ["list", "Candidatures"],
              ["stats", "Statistiques"],
            ].map(([id, label]) => (
              <button
                type="button"
                key={id}
                aria-pressed={view === id}
                onClick={() => setView(id)}
                className={`rounded-full px-4 py-2 text-sm font-semibold ${view === id ? "bg-white text-blue-900 shadow-sm" : "text-slate-600"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div
          className="flex flex-wrap gap-2 pb-2"
          aria-label="Étapes des candidatures"
        >
          <button
            type="button"
            onClick={() => chooseFilter("all")}
            aria-pressed={filter === "all"}
            className={`min-w-[85px] flex-1 rounded-xl border px-3 py-4 text-center ${filter === "all" ? "border-blue-500 bg-blue-50 text-blue-900" : "border-slate-200 text-slate-600"}`}
          >
            <strong className="block text-2xl">{applications.length}</strong>
            <span className="mt-1 block text-sm">Toutes</span>
          </button>
          {allStages.map((status) => (
            <button
              type="button"
              key={status}
              aria-pressed={filter === status}
              onClick={() => chooseFilter(status)}
              className={`min-w-[85px] flex-1 rounded-xl border px-3 py-4 text-center ${filter === status ? "border-blue-500 bg-blue-50 text-blue-900" : "border-slate-200 text-slate-600"}`}
            >
              <strong
                className={`block text-2xl ${counts(status) ? "text-blue-800" : "text-slate-400"}`}
              >
                {counts(status)}
              </strong>
              <span className="mt-1 block text-xs">
                {status}
              </span>
            </button>
          ))}
        </div>
      </div>
      {view === "stats" ? (
        <div className="grid gap-5 md:grid-cols-2">
          {[
            ["Répartition par statut", allStages, "status"],
            [
              "Origine des candidatures",
              [
                ...new Set(
                  applications.map((c) => c.source || "Origine non renseignée"),
                ),
              ],
              "source",
            ],
          ].map(([title, values, key]) => (
            <section key={key} className="rounded-2xl border bg-white p-5">
              <h4 className="mb-4 font-semibold">{title}</h4>
              {values.map((value) => {
                const count = applications.filter(
                  (c) => (c[key] || "Origine non renseignée") === value,
                ).length;
                return (
                  <div key={value} className="mb-3">
                    <p className="mb-1 flex justify-between text-sm">
                      <span>{value}</span>
                      <strong>{count}</strong>
                    </p>
                    <div className="h-2 rounded bg-slate-100">
                      <div
                        className="h-2 rounded bg-blue-700"
                        style={{
                          width: `${applications.length ? (count / applications.length) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
              {!applications.length && <p>Aucune candidature.</p>}
            </section>
          ))}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4">
            <input
              aria-label="Rechercher une candidature"
              placeholder="Nom, téléphone, e-mail, ville, origine…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelection([]);
              }}
              className={`${field} min-w-0 flex-1`}
            /><SearchPending pending={searchPending} />
            <label className="flex items-center gap-2 text-sm">
              Trier par
              <select
                className={field}
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                <option value="recent">
                  Date de candidature (plus récentes)
                </option>
                <option value="oldest">
                  Date de candidature (plus anciennes)
                </option>
                <option value="status">Statut</option>
                <option value="name">Nom du candidat</option>
              </select>
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                aria-label="Sélectionner toutes les candidatures affichées"
                checked={
                  visible.length > 0 &&
                  visible.every((c) => selection.includes(String(c._id)))
                }
                disabled={busy || !visible.length}
                onChange={(e) =>
                  setSelection(
                    e.target.checked ? visible.map((c) => String(c._id)) : [],
                  )
                }
              />
              {selection.length
                ? `${selection.length} sélectionnée(s)`
                : "Tout sélectionner"}
            </label>
            <select
              aria-label="Statut groupé"
              value={bulk}
              onChange={(e) => setBulk(e.target.value)}
              disabled={busy || !selection.length}
              className={field}
            >
              <option value="">Changer le statut…</option>
              {[...new Set([...stages, ...applicationStatuses])].map(
                (status) => (
                  <option key={status}>{status}</option>
                ),
              )}
            </select>
            <button
              type="button"
              disabled={busy || !bulk || !selection.length}
              onClick={applyBulk}
              className="rounded-xl bg-blue-800 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
            >
              Appliquer
            </button>
            <span className="ml-auto text-sm text-slate-500">
              {visible.length} candidature(s) affichée(s)
            </span>
          </div>
          <div className="space-y-3">
            {visible.map((candidate) => {
              const name =
                [candidate.firstname, candidate.lastname]
                  .filter(Boolean)
                  .join(" ") || "Candidat";
              const id = String(candidate._id);
              return (
                <article
                  key={id}
                  className="rounded-2xl border border-slate-200 bg-white p-5"
                >
                  <div className="flex flex-wrap items-start gap-4">
                    <input
                      type="checkbox"
                      aria-label={`Sélectionner ${name}`}
                      checked={selection.includes(id)}
                      disabled={busy}
                      onChange={() => toggle(id)}
                      className="mt-3"
                    />
                    <span
                      aria-hidden="true"
                      className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-lg font-bold text-blue-900"
                    >
                      {candidate.firstname?.[0]}
                      {candidate.lastname?.[0]}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-lg font-bold text-slate-900">
                        {name}
                        {candidate.town && (
                          <span className="ml-2 text-sm font-normal text-slate-500">
                            · {candidate.town}
                          </span>
                        )}
                      </h4>
                      <p className="mt-1 text-sm text-slate-500">
                        {stamp(candidate.date)
                          ? `A postulé le ${new Date(candidate.date).toLocaleDateString("fr-FR")}`
                          : "Date de candidature non renseignée"}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
                        {candidate.phone && (
                          <a href={`tel:${candidate.phone}`}>
                            {candidate.phone}
                          </a>
                        )}
                        {candidate.email && (
                          <a href={`mailto:${candidate.email}`}>
                            {candidate.email}
                          </a>
                        )}
                      </div>
                    </div>
                    <select
                      aria-label={`Statut de ${name}`}
                      disabled={busy}
                      value={candidate.status}
                      onChange={(e) =>
                        onChangeStatus(candidate, e.target.value)
                      }
                      className={`${field.replace("bg-white", "")} font-semibold ${statusStyle(candidate.status)}`}
                    >
                      {[
                        ...new Set([
                          ...stages,
                          ...applicationStatuses,
                          candidate.status,
                        ]),
                      ].map((status) => (
                        <option key={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
                    <label className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                      Origine
                      <select
                        aria-label={`Origine de ${name}`}
                        value={candidate.source || ""}
                        disabled={busy}
                        onChange={(e) =>
                          onChangeStatus(
                            candidate,
                            candidate.status,
                            e.target.value,
                          )
                        }
                        className={field}
                      >
                        <option value="">Origine non renseignée</option>
                        {[
                          ...new Set([
                            ...sources,
                            ...(candidate.source ? [candidate.source] : []),
                          ]),
                        ].map((source) => (
                          <option key={source}>{source}</option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onViewCv(candidate)}
                      className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-blue-900 hover:bg-blue-50"
                    >
                      Consulter le CV
                    </button>
                  </div>
                </article>
              );
            })}
            {!visible.length && (
              <p className="rounded-xl border bg-white p-8 text-center text-slate-500">
                Aucune candidature pour ces critères.
              </p>
            )}
          </div>
          <p className="text-xs text-slate-500">
            Les candidatures du site et de Mon Intérim sont reçues ici. France
            Travail et HelloWork ne sont pas synchronisés.
          </p>
        </>
      )}
    </section>
  );
}
